import React, { useMemo, useState, useEffect } from "react";
import { useGlobalContext } from "../Config";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";

const DetallePeriodo = () => {
  const { baseUrl } = useGlobalContext();
  const { facturadorUrl } = useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);

  const [empresaId, setEmpresaId] = useState(null);

  // ✅ Nuevo: periodo por dropdowns
  const [anio, setAnio] = useState(null); // 2024..2026
  const [mes, setMes] = useState(null); // 1..12

  const [items, setItems] = useState([]);
  const [boletasInfo, setBoletasInfo] = useState([]);
  const [lastQuery, setLastQuery] = useState(null);

  const empresasOptions = [
    { label: "ALTAMIRA", value: 1 },
    { label: "LA SIEMBRA", value: 2 },
  ];

  const mesesOptions = [
    { label: "Enero", value: 1 },
    { label: "Febrero", value: 2 },
    { label: "Marzo", value: 3 },
    { label: "Abril", value: 4 },
    { label: "Mayo", value: 5 },
    { label: "Junio", value: 6 },
    { label: "Julio", value: 7 },
    { label: "Agosto", value: 8 },
    { label: "Septiembre", value: 9 },
    { label: "Octubre", value: 10 },
    { label: "Noviembre", value: 11 },
    { label: "Diciembre", value: 12 },
  ];

  const aniosOptions = [2024, 2025, 2026].map((y) => ({
    label: String(y),
    value: y,
  }));

  const buildPeriodo = () => {
    if (!anio || !mes) return "";
    return `${anio}-${String(mes).padStart(2, "0")}`;
  };

  const PRESTADOR_RUT_BY_EMPRESA = {
    1: "76.071.007-5",
    2: "76.066.122-8",
  };

  const joinUrl = (base, path) =>
    `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(
      /^\/+/,
      ""
    )}`;

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: "include",
      redirect: "follow",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.respuesta === false) {
      const msg = data?.mensaje || `Error HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  const validar = () => {
    if (!empresaId) {
      alert("Debe seleccionar empresa.");
      return false;
    }
    if (!anio || !mes) {
      alert("Debe seleccionar mes y año.");
      return false;
    }
    return true;
  };

  const fmtMoneyCLP = (v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return "-";
    return n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  };

  const headerCard = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <div>
          <h4 style={{ margin: 0, color: "#495057" }}>Detalle Período</h4>
          <small style={{ color: "#6c757d" }}>
            Consulta el detalle de las mediciones en un periodo especificado
          </small>
        </div>
      </div>
    ),
    []
  );

  const tablaProps = {
    responsiveLayout: "scroll",
    className: "p-datatable-sm",
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 20],
    stripedRows: true,
    showGridlines: true,
  };

  // --- Merge boletas guardadas en items ---
  const keyBoleta = (remarcadorId, tipoServicio) =>
    `${String(remarcadorId ?? "")}||${String(
      tipoServicio ?? ""
    ).toUpperCase()}`;

  const mergeBoletasIntoItems = (baseItems, boletasRows) => {
    const map = new Map(
      (boletasRows || []).map((b) => [
        keyBoleta(b.remarcadorId, b.tipoServicio),
        b,
      ])
    );

    return (baseItems || []).map((it) => ({
      ...it,
      boleta: map.get(keyBoleta(it.remarcadorId, it.tipoServicio)) || null,
    }));
  };

  const cargarBoletasGuardadas = async (empId, per) => {
    const urlBoletas =
      `${baseUrl}detallePeriodo/boletas` +
      `?empresaId=${encodeURIComponent(empId)}` +
      `&periodo=${encodeURIComponent(per)}`;

    const boletasData = await fetchJson(urlBoletas, { method: "GET" });
    const boletasRows = Array.isArray(boletasData?.items)
      ? boletasData.items
      : [];

    setBoletasInfo(boletasRows);
    return boletasRows;
  };

  const buscar = async () => {
    if (!validar()) return;

    const periodo = buildPeriodo();

    setIsLoading(true);
    try {
      const url =
        `${baseUrl}detallePeriodo` +
        `?empresaId=${encodeURIComponent(empresaId)}` +
        `&periodo=${encodeURIComponent(periodo)}`;

      const data = await fetchJson(url, { method: "GET" });
      const baseItems = Array.isArray(data?.items) ? data.items : [];

      // traer boletas guardadas (si existen)
      let boletasRows = [];
      try {
        boletasRows = await cargarBoletasGuardadas(empresaId, periodo);
      } catch (e) {
        boletasRows = [];
        setBoletasInfo([]);
      }

      setItems(mergeBoletasIntoItems(baseItems, boletasRows));
      setLastQuery({ empresaId, periodo });
    } catch (e) {
      console.error(e);
      alert("Error consultando detalle periodo: " + (e?.message || e));
      setItems([]);
      setBoletasInfo([]);
      setLastQuery(null);
    } finally {
      setIsLoading(false);
    }
  };

  const limpiar = () => {
    setEmpresaId(null);
    setAnio(null);
    setMes(null);
    setItems([]);
    setBoletasInfo([]);
    setLastQuery(null);
  };

  // --- PDF Facturador (endpoint real) ---
  const abrirPdfFacturador = (boleta) => {
    if (!boleta?.documentoId) {
      alert("No hay documentoId para esta boleta.");
      return;
    }
    const url = joinUrl(
      facturadorUrl,
      `descargar/boleta/pdf?idDocumento=${encodeURIComponent(
        boleta.documentoId
      )}`
    );
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // --- helpers periodo anterior ---
  const getPrevPeriodo = (p) => {
    const m = String(p || "").match(/^(\d{4})-(\d{2})$/);
    if (!m) return null;
    let y = Number(m[1]);
    let mm = Number(m[2]);
    mm -= 1;
    if (mm === 0) {
      mm = 12;
      y -= 1;
    }
    return `${y}-${String(mm).padStart(2, "0")}`;
  };

  // --- GENERACION DE BOLETAS ---
  const generarBoletas = async () => {
    const periodo = buildPeriodo();

    if (!empresaId || !periodo) {
      alert("Selecciona empresa y periodo.");
      return;
    }
    if (!items?.length) {
      alert("No hay items para generar boletas.");
      return;
    }

    setIsLoading(true);
    try {
      const rutEmpresa = PRESTADOR_RUT_BY_EMPRESA[empresaId];
      if (!rutEmpresa) throw new Error("Falta configurar RUT del prestador.");

      // 1) historial batch (medidores)
      const reqHist = {
        periodo,
        items: items.map((it) => ({
          codigoDomicilio: it.codigoDomicilio,
          tipoServicio: it.tipoServicio,
        })),
      };

      const endpointHistorialBatch = `${baseUrl}detallePeriodo/historial/batch`;

      const histData = await fetchJson(endpointHistorialBatch, {
        method: "POST",
        body: JSON.stringify(reqHist),
      });

      const histItems = Array.isArray(histData?.items) ? histData.items : [];

      // ojo: este map es por codigoDomicilio+tipoServicio, no por remarcador
      const keyHist = (codigoDomicilio, tipoServicio) =>
        `${String(codigoDomicilio ?? "")}||${String(
          tipoServicio ?? ""
        ).toUpperCase()}`;

      const histMap = new Map(
        histItems.map((h) => [keyHist(h.codigoDomicilio, h.tipoServicio), h])
      );

      // 2) construir boletas para Facturador
      const prev = getPrevPeriodo(periodo);

      const boletas = items.map((it) => {
        const h = histMap.get(keyHist(it.codigoDomicilio, it.tipoServicio));
        const historial = Array.isArray(h?.historialConsumo)
          ? [...h.historialConsumo].sort((a, b) =>
              String(a?.periodo || "").localeCompare(String(b?.periodo || ""))
            )
          : [];

        // consumo anterior (según historial ya ordenado)
        const consumoMesAnterior =
          historial.find((x) => x?.periodo === prev)?.consumo ?? 0;

        return {
          remarcadorId: it.remarcadorId,
          tipoServicio: it.tipoServicio,
          unidad: it.unidad,

          valorUnitario: it.valorUnitario,

          cliente: it.cliente,

          lecturaMesAnterior: it.lecturaMesAnterior ?? 0,
          lecturaMesActual: it.lecturaMesActual ?? 0,

          consumoMesAnterior: consumoMesAnterior ?? 0,
          consumoMesActual: it.consumoMesActual ?? 0,

          saldoAnterior: 0,

          historialConsumo: historial,
        };
      });

      const payloadFacturador = {
        periodo,
        prestador: { rutEmpresa },
        boletas,
      };

      // 3) POST facturador
      const facturadorBatchEndpoint = joinUrl(
        facturadorUrl,
        "api/boletas/batch"
      );

      const resp = await fetchJson(facturadorBatchEndpoint, {
        method: "POST",
        body: JSON.stringify(payloadFacturador),
      });

      const factPayload = resp?.payload || null;
      const factItems = Array.isArray(factPayload?.items)
        ? factPayload.items
        : [];

      // 4) Guardar resultado en Medidores
      const guardarReq = {
        empresaId,
        periodo,
        items: factItems.map((x) => ({
          remarcadorId: x.remarcadorId,
          tipoServicio: x.tipoServicio,
          documentoId: x.documentoId ?? null,
          adjuntoPdfId: x.adjuntoPdfId ?? null,
          folio: x.folio ?? null,
          status: x.status ?? null,
          errores: Array.isArray(x.errores) ? x.errores : [],
        })),
      };

      await fetchJson(`${baseUrl}detallePeriodo/boletas/guardar-batch`, {
        method: "POST",
        body: JSON.stringify(guardarReq),
      });

      // 5) Refrescar boletas guardadas y merge
      const boletasRows = await cargarBoletasGuardadas(empresaId, periodo);
      setItems((prevItems) => mergeBoletasIntoItems(prevItems, boletasRows));

      alert(
        factPayload?.mensajeGeneral ||
          resp?.mensaje ||
          "Batch procesado correctamente."
      );
      console.log("Resp facturador:", resp);
    } catch (e) {
      console.error(e);
      alert("Error generando boletas: " + (e?.message || e));
    } finally {
      setIsLoading(false);
    }
  };

  const periodoLabel = buildPeriodo() || "—";

  useEffect(() => {
    // Preselección: mes/año actual (según reloj del navegador)
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1; // 1..12

    // opcional: solo si cae dentro del rango permitido
    if (y >= 2024 && y <= 2026) {
      setAnio(y);
      setMes(m);
    }
  }, []);

  return (
    <div className="col-12 md:col-12">
      <div
        style={{
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
          marginBottom: "20px",
        }}
      >
        {headerCard}
      </div>

      {/* filtros */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-4">
            <label style={{ fontWeight: "bold" }}>Empresa</label>
            <Dropdown
              value={empresaId}
              options={empresasOptions}
              onChange={(e) => setEmpresaId(e.value)}
              placeholder="Seleccione empresa"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
            />
          </div>

          {/* ✅ Nuevo: Mes + Año */}
          <div className="col-12 md:col-4">
            <label style={{ fontWeight: "bold" }}>Periodo</label>
            <div style={{ display: "flex", gap: 10 }}>
              <Dropdown
                value={mes}
                options={mesesOptions}
                onChange={(e) => setMes(e.value)}
                placeholder="Mes"
                style={{ width: "60%" }}
                showClear
                disabled={isLoading}
              />
              <Dropdown
                value={anio}
                options={aniosOptions}
                onChange={(e) => setAnio(e.value)}
                placeholder="Año"
                style={{ width: "40%" }}
                showClear
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="col-12 md:col-4">
            <label style={{ fontWeight: "bold", opacity: 0 }}>Acciones</label>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                height: "42px",
                marginTop: 4,
              }}
            >
              <Button
                label="Buscar"
                icon="pi pi-search"
                onClick={buscar}
                disabled={isLoading}
              />
              <Button
                label="Limpiar"
                icon="pi pi-times"
                className="p-button-text"
                onClick={limpiar}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <ProgressSpinner />
        </div>
      )}

      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div
          style={{
            marginBottom: 12,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong>Resultado</strong>
            <div style={{ color: "#6c757d", fontSize: 13 }}>
              {lastQuery ? `Periodo ${lastQuery.periodo}` : "Sin consulta"}
              {" · "}
              {items.length} registros
            </div>
          </div>

          {items.length > 0 && (
            <Button
              label="Generar boletas"
              icon="pi pi-send"
              onClick={generarBoletas}
              disabled={!items?.length || isLoading}
              className="p-button-success p-button-sm"
            />
          )}
        </div>

        <DataTable
          value={items}
          dataKey={(row) =>
            `${row?.codigoDomicilio ?? "NA"}|${row?.tipoServicio ?? "NA"}|${
              row?.remarcadorId ?? "NA"
            }`
          }
          {...tablaProps}
        >
          <Column field="codigoDomicilio" header="Código" sortable />
          <Column field="domicilio" header="Domicilio" sortable />
          <Column
            header="Cliente"
            body={(row) => row?.cliente?.nombreRazonSocial ?? "-"}
            sortable
          />
          <Column header="RUT" body={(row) => row?.cliente?.rut ?? "-"} />
          <Column field="tipoServicio" header="Servicio" sortable />
          <Column field="unidad" header="Unidad" />
          <Column field="lecturaMesAnterior" header="Lect. ant." />
          <Column field="lecturaMesActual" header="Lect. act." />
          <Column field="consumoMesActual" header="Consumo" sortable />

          <Column
            header="Valor unit."
            body={(row) => `$ ${fmtMoneyCLP(row?.valorUnitario)}`}
            sortable
          />
          <Column
            header="Monto"
            body={(row) => `$ ${fmtMoneyCLP(row?.montoFinal)}`}
            sortable
          />

          <Column
            header="Boleta"
            body={(row) => {
              const b = row?.boleta;
              if (!b || !b.documentoId) return "—";
              return (
                <Button
                  label="Ver PDF"
                  icon="pi pi-file-pdf"
                  className="p-button-text p-button-sm"
                  onClick={() => abrirPdfFacturador(b)}
                />
              );
            }}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default DetallePeriodo;
